import React from 'react';

/**
 * Cloudbeddy brand logo - the official WHITE lockup, used AS-IS (no box, no
 * shape around it). The PNG is tightly cropped (`cb-logo-white.png`) and
 * already contains the mark + "CloudBeddy" wordmark + tagline, so we never
 * add separate text beside it.
 *
 * Because the art is white, it must sit on a dark / brand-green surface - every
 * header that uses it provides that background. For the rare light surface,
 * pass `tone="dark"` to render the green wordmark fallback instead.
 *
 * Props:
 *  - size: 'sm' | 'md' | 'lg' | 'xl'  (controls height; width auto)
 *  - tone: 'light' (white logo, default) | 'dark' (green text, for light bg)
 *  - className / onClick passthrough
 *
 * Legacy props (`variant`, `withWordmark`, `wordmarkClassName`, `tagline`) are
 * accepted and ignored so existing call sites keep working.
 */
const LOGO_SRC = '/assets/logos/cb-logo-white.png';

const HEIGHTS = {
  sm: 'h-7 sm:h-8',
  md: 'h-9 sm:h-10',
  lg: 'h-12 sm:h-14',
  xl: 'h-16 sm:h-20',
};

const TEXT = {
  sm: 'text-lg',
  md: 'text-xl',
  lg: 'text-3xl',
  xl: 'text-4xl',
};

export default function Logo({ size = 'md', tone = 'light', className = '', onClick }) {
  const clickable = typeof onClick === 'function';
  const base = `inline-flex items-center ${clickable ? 'cursor-pointer' : ''} ${className}`;

  if (tone === 'dark') {
    // Light-background fallback: the white PNG would be invisible, so render the
    // brand wordmark in green instead.
    return (
      <span className={base} onClick={onClick} role={clickable ? 'button' : undefined}>
        <span className={`font-display font-extrabold tracking-tight ${TEXT[size] || TEXT.md}`}>
          <span className="text-teal-950">Cloud</span>
          <span className="text-gradient-green">beddy</span>
        </span>
      </span>
    );
  }

  return (
    <span className={base} onClick={onClick} role={clickable ? 'button' : undefined}>
      <img
        src={LOGO_SRC}
        alt="Cloudbeddy"
        className={`${HEIGHTS[size] || HEIGHTS.md} w-auto object-contain select-none`}
        draggable="false"
      />
    </span>
  );
}
